// Updated: Added conditional participants step for tours requiring park confirmation
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Clock, Hourglass, Calendar } from 'lucide-react';
import { formatTime, formatDate } from '../utils/dateUtils';
import { supabase } from '../lib/supabase';
import { Tour } from '../types/tours';
import { BookingParticipant } from '../types/bookings';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import BookingStepper from '../components/booking/BookingStepper';
import BookingTourCard from '../components/booking/BookingTourCard';
import BookingDateStep from '../components/booking/BookingDateStep';
import BookingGroupStep from '../components/booking/BookingGroupStep';
import BookingParticipantsStep from '../components/booking/BookingParticipantsStep';
import BookingContactStep from '../components/booking/BookingContactStep';
import BookingConfirmStep from '../components/booking/BookingConfirmStep';
import BookingSuccessStep from '../components/booking/BookingSuccessStep';
import LoadingSpinner from '../components/LoadingSpinner';

type Step = 'date' | 'group' | 'participants' | 'contact' | 'confirm' | 'success';

const BookingPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [tour, setTour] = useState<Tour | null>(null);
  const [currentStep, setCurrentStep] = useState<Step>('date');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [bookingData, setBookingData] = useState({
    date: searchParams.get('date') ? new Date(searchParams.get('date')!) : null,
    groupType: null as 'group' | 'reduced' | 'private' | null,
    persons: 1,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    pickupAddress: 'Las Vegas Restaurant in Sierpe',
    totalPrice: 0,
    termsAccepted: false,
    paymentMethod: 'cash' as 'cash' | 'paypal',
    bookingCode: '',
    bootSizes: [] as string[],
    participants: [] as BookingParticipant[]
  });

  const steps = useMemo(() => {
    const base: { id: Step; label: string }[] = [
      { id: 'date', label: 'Select Date' },
      { id: 'group', label: 'Persons' },
    ];
    if (tour?.requires_participant_details) {
      base.push({ id: 'participants', label: 'Participants' });
    }
    base.push(
      { id: 'contact', label: 'Contact Info' },
      { id: 'confirm', label: 'Confirm' },
      { id: 'success', label: 'Success' }
    );
    return base;
  }, [tour?.requires_participant_details]);

  useEffect(() => {
    const fetchTour = async () => {
      try {
        if (!slug) {
          throw new Error('Tour slug is required');
        }

        const { data, error } = await supabase
          .from('tours')
          .select(`
            *,
            tour_images (
              id,
              image_url,
              image_type,
              display_order
            )
          `)
          .eq('slug', slug)
          .eq('status', 'published')
          .single();

        if (error) {
          console.error('Supabase error:', error);
          throw new Error('Failed to fetch tour data');
        }

        if (!data) {
          throw new Error('Tour not found');
        }

        if (!data.id) {
          throw new Error('Invalid tour data');
        }

        setTour(data);
      } catch (err) {
        console.error('Error fetching tour:', err);
        setError(err instanceof Error ? err.message : 'Failed to load tour');
        navigate('/');
      } finally {
        setLoading(false);
      }
    };

    fetchTour();
  }, [slug, navigate]);

  const handleNext = () => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1].id);
    }
  };

  const handleBack = () => {
    const currentIndex = steps.findIndex(step => step.id === currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1].id);
    }
  };

  if (loading) {
    return (
      <Layout hideFooter>
        <div className="min-h-screen bg-[#2A382A] pt-16 md:pt-32 pb-24 md:pb-16 flex items-center justify-center">
          <div className="bg-white rounded-xl w-full max-w-sm mx-4">
            <LoadingSpinner />
          </div>
        </div>
      </Layout>
    );
  }

  if (error || !tour) {
    return (
      <Layout hideFooter>
        <div className="min-h-screen bg-[#2A382A] pt-16 md:pt-32 pb-24 md:pb-16 flex items-center justify-center">
          <div className="bg-white rounded-xl p-8 w-full max-w-sm mx-4 text-[#2A382A]">
            <p className="text-center font-medium">
              {error || 'Tour not found'}
            </p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout hideFooter>
      <div className="min-h-screen bg-[#2A382A] pt-16 md:pt-32 pb-24 md:pb-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <BookingStepper steps={steps} currentStep={currentStep} />

            <div className="mt-6 grid grid-cols-1 lg:grid-cols-[10fr_2fr] gap-8">
              {/* Main Content */}
              <div>
                <div className="bg-white rounded-xl p-8">
                  {currentStep === 'date' && (
                    <BookingDateStep
                      tour={tour}
                      selectedDate={bookingData.date}
                      onDateSelect={(date) => setBookingData({ ...bookingData, date })}
                      onNext={handleNext}
                    />
                  )}
                  {currentStep === 'group' && (
                    <BookingGroupStep
                      tour={tour}
                      groupType={bookingData.groupType}
                      persons={bookingData.persons}
                      onChange={(data) => setBookingData({ ...bookingData, ...data })}
                      onBack={handleBack}
                      onNext={handleNext}
                    />
                  )}
                  {currentStep === 'participants' && (
                    <BookingParticipantsStep
                      tour={tour}
                      persons={bookingData.persons}
                      participants={bookingData.participants}
                      onChange={(participants) => setBookingData({ ...bookingData, participants })}
                      onBack={handleBack}
                      onNext={handleNext}
                    />
                  )}
                  {currentStep === 'contact' && (
                    <BookingContactStep
                      tour={tour}
                      data={bookingData}
                      onChange={(data) => setBookingData({ ...bookingData, ...data })}
                      onBack={handleBack}
                      onNext={handleNext}
                    />
                  )}
                  {currentStep === 'confirm' && (
                    <BookingConfirmStep
                      tour={tour}
                      data={bookingData}
                      onChange={(updates) => setBookingData({ ...bookingData, ...updates })}
                      onBack={handleBack}
                      onNext={handleNext}
                    />
                  )}
                  {currentStep === 'success' && (
                    <BookingSuccessStep
                      tour={tour}
                      data={{
                        ...bookingData,
                        bookingCode: bookingData.bookingCode || `SF-${Math.random().toString(36).substring(2, 10).toUpperCase()}`
                      }}
                    />
                  )}
                </div>
              </div>

              {/* Tour Card */}
              <div>
                <BookingTourCard tour={tour} bookingData={bookingData} />
              </div>

              {/* Mobile Tour Summary */}
              <div className="fixed bottom-0 left-0 right-0 lg:hidden z-50">
                <div className="bg-white border-t border-[#2A382A]/10 shadow-lg">
                  <div className="p-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={tour.tour_images?.find(img => img.image_type === 'carousel' && img.display_order === 0)?.image_url}
                        alt={tour.name}
                        className="w-16 h-16 rounded-lg object-cover"
                      />
                      <div className="flex-1">
                        <h3 className="font-semibold text-[#2A382A] text-sm">{tour.name}</h3>
                        {bookingData.date && (
                          <p className="flex items-center gap-1 text-xs text-[#2A382A]/70 mt-0.5">
                            <span className="w-5 h-5 bg-[#2A382A] rounded-[0.2rem] flex items-center justify-center">
                              <Calendar className="w-3 h-3 text-white" />
                            </span>
                            {formatDate(bookingData.date, {
                              weekday: 'long',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        )}
                        <div className="flex gap-4 mt-1">
                          <div className="flex items-center gap-1 text-xs text-[#2A382A]/70">
                            <span className="w-5 h-5 bg-[#2A382A] rounded-[0.2rem] flex items-center justify-center">
                              <Clock className="w-3 h-3 text-white" />
                            </span>
                            <span>{formatTime(tour.departure).americanTime}</span>
                          </div>
                          <div className="flex items-center gap-1 text-xs text-[#2A382A]/70">
                            <span className="w-5 h-5 bg-[#2A382A] rounded-[0.2rem] flex items-center justify-center">
                              <Hourglass className="w-3 h-3 text-white" />
                            </span>
                            <span>{tour.duration}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 text-[#2A382A]/40">
                        {bookingData.groupType && (
                          <div className="bg-[#953B0E]/10 px-3 py-1 rounded-full">
                            <span className="text-[#953B0E] font-semibold">${bookingData.totalPrice}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default BookingPage;